import {fetchOk} from "#utils/fetch-ok"
import {PriorityQueue} from "#utils/priority-queue"

export class DownloadQueue {
    constructor(private _maxConcurrentDownloads = 24) {}

    async download(url: string, priority?: number, abortSignal?: AbortSignal): Promise<ArrayBuffer> {
        priority ??= 0
        return new Promise<ArrayBuffer>((resolve, reject) => {
            this.scheduleDownloadTask({
                url,
                priority,
                abortSignal,
                resolve,
                reject,
            })
        })
    }

    set maxConcurrentDownloads(value: number) {
        if (value < 1) {
            throw new Error("maxConcurrentDownloads must be at least 1")
        }
        this._maxConcurrentDownloads = value
    }

    get maxConcurrentDownloads(): number {
        return this._maxConcurrentDownloads
    }

    private scheduleDownloadTask(task: DownloadTask) {
        this._scheduledDownloadTasks.enqueue(task, task.priority)
        this.processDownloadQueue()
    }

    private updateTaskPriority(task: DownloadTask, newPriority: number) {
        // we can only update priority if it hasn't started yet
        if (this._scheduledDownloadTasks.contains(task)) {
            this._scheduledDownloadTasks.updatePriority(task, newPriority)
        }
    }

    private processDownloadQueue() {
        while (this._activeDownloadTasks.size < this._maxConcurrentDownloads) {
            const task = this._scheduledDownloadTasks.dequeue()
            if (!task) {
                break
            }
            this._activeDownloadTasks.add(task)
            this.executeDownload(task)
                .then((data) => {
                    task.resolve(data)
                })
                .catch((error) => {
                    task.reject(error)
                })
                .finally(() => {
                    this._activeDownloadTasks.delete(task)
                    this.processDownloadQueue()
                })
        }
        // console.log("DownloadService: Active downloads:", this._activeDownloadTasks.size, "Scheduled downloads:", this._scheduledDownloadTasks.size)
    }

    private async executeDownload(task: DownloadTask): Promise<ArrayBuffer> {
        const response = await fetchOk(task.url, {signal: task.abortSignal})
        return response.arrayBuffer()
    }

    private _scheduledDownloadTasks = new PriorityQueue<DownloadTask>()
    private _activeDownloadTasks = new Set<DownloadTask>()
}

type DownloadTask = {
    url: string
    priority: number
    abortSignal?: AbortSignal
    resolve: (data: ArrayBuffer) => void
    reject: (error: unknown) => void
}
